import React, { useMemo, useState, useEffect } from 'react';
import { ConnectionPhase } from '../../services/connectionJourneyService';
import { useToast } from '../ui/Toast';
import { updatePresence } from '../../services/presenceService';

interface VideoPhaseProps {
  currentPhase: ConnectionPhase;
  /** Sorted participant UIDs from connectionFlow.participants */
  participants: string[];
  /** Current user's UID */
  currentUserId: string;
  /** Current user's display name */
  currentUserName: string;
  /** Fallback: stored meetUrl from Firestore (used ONLY if participants are unavailable) */
  meetingUrl?: string | undefined;
  onEndVideo: () => Promise<void>;
  connectionId?: string;
}

/**
 * ✅ EMBEDDED VIDEO CALL (Jitsi iframe)
 * 
 * IMPORTANT: This component generates the Jitsi URL LOCALLY from participant IDs.
 * This ensures BOTH users ALWAYS join the SAME room, regardless of Firestore sync timing.
 * 
 * Room name formula: superkonnected-{sorted_uid1_8chars}-{sorted_uid2_8chars}
 * This is the SAME formula used in meetService.ts and JitsiCall.tsx.
 */
const VideoPhase: React.FC<VideoPhaseProps> = ({
  currentPhase,
  participants,
  currentUserId,
  currentUserName,
  meetingUrl,
  onEndVideo,
  connectionId,
}) => {
  // ✅ UX: Loading and error states
  const [isLoading, setIsLoading] = useState(true);
  const [iframeError, setIframeError] = useState(false);
  const { error: showError } = useToast();
  
  // ✅ Update presence when in video call
  useEffect(() => {
    if (currentPhase === 'video' && connectionId && currentUserId && currentUserName) {
      updatePresence(connectionId, currentUserId, currentUserName, 'in_video');
      
      // Reset to idle when component unmounts or phase changes
      return () => {
        updatePresence(connectionId, currentUserId, currentUserName, 'idle');
      };
    }
  }, [currentPhase, connectionId, currentUserId, currentUserName]);
  
  // ==========================================
  // ✅ Generate Jitsi URL LOCALLY from participant IDs
  // This ensures both users ALWAYS get the SAME room name
  // ==========================================
  const jitsiUrl = useMemo(() => {
    // Don't generate URL if not in video phase
    if (currentPhase !== 'video') {
      return null;
    }

    // Generate deterministic room name from sorted participant IDs
    const sortedParticipants = [...(participants || [])].sort();
    
    let roomName: string;
    if (sortedParticipants.length >= 2) {
      const shortId = sortedParticipants.map(id => id.slice(0, 8)).join('-');
      roomName = `superkonnected-${shortId}`;
    } else if (meetingUrl) {
      // Fallback: extract room name from stored URL
      try {
        const urlPath = new URL(meetingUrl).pathname;
        roomName = urlPath.replace(/^\//, ''); // Remove leading slash
      } catch {
        roomName = `superkonnected-fallback-${Date.now()}`;
      }
    } else {
      return null; // Can't determine room
    }

    // Build Jitsi config (same as meetService.ts)
    // Note: String values MUST be JSON-stringified before URL-encoding,
    // otherwise Jitsi's internal JSON.parse() will fail.
    const meetingSubject = 'SuperKonnected videogesprek';
    const jitsiConfig = [
      'config.prejoinConfig.enabled=false',
      'config.prejoinPageEnabled=false',
      'config.requireDisplayName=false',
      'config.startWithAudioMuted=false',
      'config.startWithVideoMuted=false',
      `config.subject=${encodeURIComponent(JSON.stringify(meetingSubject))}`,
      'config.disableDeepLinking=true',
      'config.enableWelcomePage=false',
      'config.enableClosePage=false',
      'config.enableInsecureRoomNameWarning=false',
      'config.enableLobbyChat=false',
      'config.hideLobbyButton=true',
      'config.p2p.enabled=true',
      'interfaceConfig.SHOW_JITSI_WATERMARK=false',
      'interfaceConfig.SHOW_WATERMARK_FOR_GUESTS=false',
      'interfaceConfig.SHOW_BRAND_WATERMARK=false',
      'interfaceConfig.SHOW_POWERED_BY=false',
      'interfaceConfig.SHOW_PROMOTIONAL_CLOSE_PAGE=false',
      'interfaceConfig.SHOW_CHROME_EXTENSION_BANNER=false',
      'interfaceConfig.HIDE_DEEP_LINKING_LOGO=true',
      'interfaceConfig.HIDE_INVITE_MORE_HEADER=true',
      'interfaceConfig.MOBILE_APP_PROMO=false',
      'interfaceConfig.DISPLAY_WELCOME_PAGE_CONTENT=false',
      'interfaceConfig.DISPLAY_WELCOME_FOOTER=false',
      `interfaceConfig.APP_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
      `interfaceConfig.NATIVE_APP_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
      `interfaceConfig.PROVIDER_NAME=${encodeURIComponent(JSON.stringify('SuperKonnected'))}`,
      `interfaceConfig.DEFAULT_BACKGROUND=${encodeURIComponent(JSON.stringify('#0A2342'))}`,
    ].join('&');

    // Each user sets their OWN display name (not the initiator's name)
    const userInfoParam = currentUserName
      ? `&userInfo.displayName=${encodeURIComponent(JSON.stringify(currentUserName))}`
      : '';

    const url = `https://video.superkonnected.com/${roomName}#${jitsiConfig}${userInfoParam}`;

    console.log(`📹 [VideoPhase] Generated Jitsi URL locally:`, {
      roomName,
      participants: sortedParticipants,
      currentUser: currentUserId?.slice(0, 8),
      url: url.substring(0, 80) + '...',
    });

    return url;
  }, [currentPhase, participants, currentUserId, currentUserName, meetingUrl]);

  // Don't render if not in video phase or no URL available
  if (currentPhase !== 'video' || !jitsiUrl) {
    return null;
  }

  return (
    <div className="w-full h-full flex flex-col p-2">
      {/* Jitsi iframe - gewone flow (geen absolute), zodat de balk eronder komt */}
      <div className="flex-1 bg-black rounded-lg overflow-hidden shadow-2xl flex relative">
        <iframe
          src={jitsiUrl}
          allow="camera; microphone; fullscreen; display-capture; autoplay"
          className="w-full h-full border-0"
          title="Jitsi Video Call"
          onLoad={() => {
            setIsLoading(false);
            console.log('📹 Video call iframe loaded successfully');
          }}
          onError={() => {
            setIframeError(true);
            setIsLoading(false);
            showError('Video call kon niet geladen worden. Probeer het opnieuw.');
            console.error('📹 Video call iframe failed to load');
          }}
        />
        
        {/* ✅ UX: Loading state */}
        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/80">
            <div className="text-center text-white">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
              <p className="text-sm">Video call laden...</p>
            </div>
          </div>
        )}
        
        {/* ✅ UX: Error state */}
        {iframeError && (
          <div className="absolute inset-0 flex items-center justify-center bg-red-900/90">
            <div className="text-center text-white p-6">
              <div className="text-4xl mb-4">⚠️</div>
              <p className="text-lg font-semibold mb-2">Fout bij laden video call</p>
              <p className="text-sm mb-4">Probeer het opnieuw of controleer je internetverbinding</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-white text-red-900 rounded-lg hover:bg-gray-100 transition-colors"
              >
                Pagina herladen
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Statische balk onder de video – alleen onze eigen beëindig-knop */}
      <div className="mt-2 pb-1 flex items-center justify-end">
        <button
          type="button"
          onClick={onEndVideo}
          aria-label="Beëindig videogesprek"
          aria-describedby="end-video-help"
          className="inline-flex items-center px-3 py-1.5 rounded-full text-xs font-medium bg-red-600 text-white hover:bg-red-700 shadow-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
        >
          📴 Beëindig videogesprek
        </button>
        <span id="end-video-help" className="sr-only">
          Sluit het videogesprek af en keer terug naar de chat
        </span>
      </div>
    </div>
  );
};

export default VideoPhase;
